import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Locale } from 'date-fns';
import { Direction, CultureContext, CultureContextValues } from './CultureProvider.context';

import { useIsomorphicEffect } from '../../hooks';
import { loadDateFnsLocale } from '../utils/loadDateFnsLocale';

export type CultureProviderProps = {
    children: React.ReactNode;

    /**
     * Controlled locale. When set, the provider will always use this value.
     */
    locale?: string;

    /**
     * Default locale (uncontrolled mode). Used only if `locale` is not provided.
     * @default en-GB
     */
    defaultLocale?: string;

    /**
     * Controlled direction. When set, the provider will always use this value.
     */
    direction?: Direction;

    /**
     * Default direction (uncontrolled mode). Used only if `direction` is not provided.
     * @default ltr
     */
    defaultDirection?: Direction;
};

export function CultureProvider(props: CultureProviderProps): React.JSX.Element {
    const { children, locale: localeProp, defaultLocale = 'en-GB', direction, defaultDirection = 'ltr' } = props;

    // Uncontrolled state for locale
    const [uncontrolledLocale, setUncontrolledLocale] = useState<string>(defaultLocale);

    // Effective locale: controlled or uncontrolled
    const effectiveLocale = localeProp ?? uncontrolledLocale;

    // Uncontrolled state for dir
    const [uncontrolledDir, setUncontrolledDir] = useState<Direction>(defaultDirection);
    const effectiveDir = direction ?? uncontrolledDir;

    // Dynamic date-fns locale object
    const [dateFnsLocale, setDateFnsLocale] = useState<Locale | null>(null);

    // Set direction (controlled/uncontrolled)
    const setDirection = useCallback(
        (dir: Direction) => {
            if (direction === undefined) {
                setUncontrolledDir(dir);
            }
            document.documentElement.setAttribute('dir', dir);
        },
        [direction]
    );

    const toggleDirection = useCallback(() => {
        setDirection(effectiveDir === 'ltr' ? 'rtl' : 'ltr');
    }, [effectiveDir, setDirection]);

    // Set locale (controlled/uncontrolled)
    const setLocale = useCallback(
        (loc: string) => {
            if (localeProp === undefined) {
                setUncontrolledLocale(loc);
            }
        },
        [localeProp]
    );

    // Sync <html dir> with effective direction
    useIsomorphicEffect(() => {
        document.documentElement.setAttribute('dir', effectiveDir);
    }, [effectiveDir]);

    // Sync <html lang> with Intl-safe date locale
    useIsomorphicEffect(() => {
        document.documentElement.setAttribute('lang', effectiveLocale);
    }, [effectiveLocale]);

    // Load date-fns locale (fallback handled inside loadDateFnsLocale)
    useEffect(() => {
        loadDateFnsLocale(effectiveLocale).then(setDateFnsLocale);
    }, [effectiveLocale]);

    // Context value
    const context: CultureContextValues = useMemo(
        () => ({
            locale: effectiveLocale, // raw user/prop locale
            dir: effectiveDir,
            dateFnsLocale,
            setDirection,
            setLocale,
            toggleDirection,
        }),
        [effectiveLocale, effectiveDir, dateFnsLocale, setDirection, setLocale, toggleDirection]
    );

    return <CultureContext.Provider value={context}>{children}</CultureContext.Provider>;
}
